import React, { useState, useEffect } from "react"
import { Form, Row, Col, Button, Spinner } from "react-bootstrap"
import useAuthAxios from "../../hooks/useAuthAxios"
import { toast } from "react-toastify"
import TimePicker from "../common/TimePicker"

const DAYS = [
  { id: 1, name: "Δευτέρα" },
  { id: 2, name: "Τρίτη" },
  { id: 3, name: "Τετάρτη" },
  { id: 4, name: "Πέμπτη" },
  { id: 5, name: "Παρασκευή" },
  { id: 6, name: "Σάββατο" },
  { id: 7, name: "Κυριακή" }
]

const unformatTime = time => {
  if (!time) return ""
  return time.replace(/:/g, "").slice(0, 4)
}

const WorkingDaysForm = ({ expertId, initialData, onChange, disabled }) => {
  const { get, post } = useAuthAxios()
  const [workingDays, setWorkingDays] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const validateWorkingDays = days => {
    const hasCompleteDays = days.some(day => day.TimeFrom && day.TimeTo)
    if (!hasCompleteDays) {
      setError("Παρακαλώ συμπληρώστε τουλάχιστον μία ημέρα με ώρα έναρξης και λήξης")
    } else {
      setError(null)
    }
    return hasCompleteDays
  }

  useEffect(() => {
    const fetchWorkingDays = async () => {
      if (!expertId) return
      if (initialData) {
        setWorkingDays(initialData.UserWorkingDaysDetails || [])
        return
      }

      setLoading(true)
      try {
        const response = await get(`/expert/${expertId}/workingDays`)
        if (response.success && response.data) {
          setWorkingDays(response.data.UserWorkingDaysDetails || [])
        }
      } catch (err) {
        console.error("Error fetching working days:", err)
        setError("Αποτυχία φόρτωσης ωραρίου εργασίας")
        toast.error("Αποτυχία φόρτωσης ωραρίου εργασίας")
      } finally {
        setLoading(false)
      }
    }

    fetchWorkingDays()
  }, [expertId, initialData, get])

  const handleSave = async () => {
    if (!expertId) return

    // Validate at least one complete day is set
    if (!validateWorkingDays(workingDays)) {
      return
    }

    setSaving(true)
    try {
      const formattedData = {
        WorkingDays: workingDays.map(day => ({
          UserWorkingDaysDetails: {
            DayID: day.DayID,
            TimeFrom: day.TimeFrom?.padStart(4, "0") || "",
            TimeTo: day.TimeTo?.padStart(4, "0") || ""
          }
        }))
      }

      const response = await post(`/expert/${expertId}/workingDays`, formattedData)
      if (response.success && true === response.data?.IsSuccess) {
        onChange()
      } else {
        toast.error(response.data?.Problem || "Αποτυχία αποθήκευσης ωραρίου εργασίας")
      }
    } catch (err) {
      console.error("Error saving working days:", err)
    } finally {
      setSaving(false)
    }
  }

  const handleDayChange = (dayId, field, value) => {
    setWorkingDays(prev => {
      const newDays = [...prev]
      const dayIndex = newDays.findIndex(d => d.DayID === dayId)

      if (dayIndex === -1 && value) {
        // Add new day
        newDays.push({
          DayID: dayId,
          TimeFrom: field === "TimeFrom" ? unformatTime(value) : "",
          TimeTo: field === "TimeTo" ? unformatTime(value) : ""
        })
      } else if (dayIndex !== -1) {
        // Update existing day
        newDays[dayIndex] = {
          ...newDays[dayIndex],
          [field]: value ? unformatTime(value) : ""
        }

        // Remove day if both times are empty
        if (!newDays[dayIndex].TimeFrom && !newDays[dayIndex].TimeTo) {
          newDays.splice(dayIndex, 1)
        }
      }

      // Run validation after updating the days
      validateWorkingDays(newDays)
      return newDays
    })
  }

  const handleClear = (dayId, field) => {
    handleDayChange(dayId, field, "")
  }

  const isInvalidDay = dayData => {
    return (dayData.TimeFrom && !dayData.TimeTo) || (!dayData.TimeFrom && dayData.TimeTo)
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2 text-muted">Φόρτωση ωραρίου εργασίας...</div>
      </div>
    )
  }

  return (
    <Form>
      {DAYS.map(day => {
        const dayData = workingDays.find(d => d.DayID === day.id) || {}
        const isInvalid = isInvalidDay(dayData)
        return (
          <Row key={day.id} className="mb-3">
            <Col md={3}>
              <Form.Label className="mb-0 fw-medium">{day.name}</Form.Label>
            </Col>
            <Col md={4}>
              <TimePicker
                label="Από"
                value={dayData.TimeFrom}
                onChange={value => handleDayChange(day.id, "TimeFrom", value)}
                disabled={disabled || saving}
                isInvalid={isInvalid}
                onClear={() => handleClear(day.id, "TimeFrom")}
              />
            </Col>
            <Col md={4}>
              <TimePicker
                label="Έως"
                value={dayData.TimeTo}
                onChange={value => handleDayChange(day.id, "TimeTo", value)}
                disabled={disabled || saving}
                isInvalid={isInvalid}
                onClear={() => handleClear(day.id, "TimeTo")}
              />
            </Col>
            {isInvalid && (
              <Col xs={12}>
                <div className="alert alert-danger py-2 mt-2 mb-0">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-exclamation-circle me-2" />
                    <small>Παρακαλώ συμπληρώστε και τις δύο ώρες ή αφήστε τες και τις δύο κενές</small>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        )
      })}
      {!disabled && (
        <>
          {error && (
            <div className="alert alert-danger mt-4">
              <i className="fas fa-exclamation-circle me-2" />
              {error}
            </div>
          )}
          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || workingDays.some(isInvalidDay) || error !== null}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Αποθήκευση...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2" />
                  Αποθήκευση
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </Form>
  )
}

export default WorkingDaysForm
